import React from 'react';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import moment from 'moment';
import {useIpDetails} from '../../../context/ipDetails';
import {formatOneDate, getMinCheckInDate} from '../../../utils/common';
import {MOBILE_SEARCH_STEPS, SEARCH_RESERVATION_FORM_NAMES} from '../constants';
import {DATE_FORMAT} from '../../../utils/date';
import {FormTypes} from '../SearchReservationScreen';
import {
  ButtonStyled,
  ConfirmButton,
  DatepickerInput,
  MobileAction,
  SearchForm,
} from '../styled';
import {Statuses} from '../../../utils/hooks';
import Input from '../../../components/acommon/FieldInput';

type SearchByBookingDetailsFormProps = {
  onSearch: (
    payload: Omit<FormTypes, SEARCH_RESERVATION_FORM_NAMES.booking_reference>,
  ) => void;
  onChangeMobileStep: (step: MOBILE_SEARCH_STEPS) => () => void;
  setStatus: (status: Statuses) => void;
  isMobileMode: boolean;
  isLoading: boolean;
};
function SearchByBookingDetailsForm({
  onSearch,
  setStatus,
  isLoading,
  isMobileMode,
  onChangeMobileStep,
}: SearchByBookingDetailsFormProps) {
  const {ipDetails} = useIpDetails();
  const {t, i18n} = useTranslation();
  const {
    control,
    trigger,
    register,
    handleSubmit,
    formState: {errors, isSubmitted},
  } = useForm<FormTypes>();

  const searchExistingReservation = async (formData: FormTypes) => {
    setStatus(Statuses.loading);
    const checkInDate = moment(
      formData[SEARCH_RESERVATION_FORM_NAMES.check_in_date],
    ).format(DATE_FORMAT);

    const payload = {
      [SEARCH_RESERVATION_FORM_NAMES.check_in_date]: checkInDate,
      [SEARCH_RESERVATION_FORM_NAMES.lead_email]:
        formData[SEARCH_RESERVATION_FORM_NAMES.lead_email],
    };
    onSearch(payload);
  };

  const validateCheckInDate = (value?: string | Date) => {
    if (!value) return value;

    const minCheckInDate = getMinCheckInDate();
    const isAfterMinCheckInDate = moment(value).isSameOrAfter(minCheckInDate);
    if (isAfterMinCheckInDate) return true;

    const formatMinCheckInDate = formatOneDate({
      date: minCheckInDate.toDate(),
      ipCountry: ipDetails?.country_code,
    });
    return t(`minimum_check_in_date_is`, {date: formatMinCheckInDate});
  };

  React.useEffect(
    function changeTranslationErrorMessages() {
      if (isSubmitted) {
        trigger();
      }
    },
    [isSubmitted, i18n.language, trigger],
  );

  return (
    <SearchForm>
      <Controller
        name={SEARCH_RESERVATION_FORM_NAMES.check_in_date}
        control={control}
        rules={{required: `${t('required')}`, validate: validateCheckInDate}}
        render={({field: {onChange, value, ...restField}, fieldState: {error}}) => (
          <DatepickerInput
            disabled={isLoading}
            error={error?.message}
            label={t('check_in_date')}
            onChange={value => onChange(value?.toString())}
            value={new Date(value)}
            {...restField}
          />
        )}
      />
      <Input
        {...register(SEARCH_RESERVATION_FORM_NAMES.lead_email, {
          required: `${t('required')}`,
        })}
        disabled={isLoading}
        label={t('lead_guest_email')}
        placeholder={t('enter_email')}
        tooltipContentKey={t('lead_guest_email_tooltip')}
        error={errors[SEARCH_RESERVATION_FORM_NAMES.lead_email]?.message}
      />
      <ConfirmButton
        onClick={handleSubmit(searchExistingReservation)}
        label={isLoading ? `${t('searching')}...` : t('continue')}
        disabled={isLoading}
      />
      {isMobileMode && (
        <MobileAction>
          <p>{t('or')}</p>
          <div>
            {t('find_my_booking_via')}
            <ButtonStyled
              label={t('booking_reference')}
              onClick={onChangeMobileStep(MOBILE_SEARCH_STEPS.bookingReference)}
              secondary
            />
          </div>
        </MobileAction>
      )}
    </SearchForm>
  );
}

export {SearchByBookingDetailsForm};
